import * as api from '@gitbook/api-client';
import { getSiteProductDefinition, METER_PRICES } from '@gitbook/app-pricing';
import { Icon } from '@gitbook/spine-icons';
import {
    Alert,
    Button,
    Dialog,
    DialogBody,
    DialogBrandedLayout,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    Heading,
    useDialogClose,
} from '@gitbook/spine-react';

import { getMonthlySitePrice, useUpgradeAddon } from '../../state';
import { priceFormatter } from '../OrganizationBilling';
import { UpgradeDialogProvider } from '../UpgradeDialog';
import { getBillingUsageAddonPricingParts } from './billingUsageAddonPricing';
import { PrettyDollarNumber } from './PrettyDollarNumber';

type EnableBillingUsageAddonDialogProps = {
    organization: api.Organization;
    meterName: api.BillingMeter;
    site?: api.Site;
    onConfirm: () => void;
};

/**
 * Dialog to enable a billing usage add-on.
 */
export function EnableBillingUsageAddonDialog(props: EnableBillingUsageAddonDialogProps) {
    return (
        <Dialog size={{ width: 832 }}>
            <UpgradeDialogProvider>
                <EnableBillingUsageAddonDialogContent {...props} />
            </UpgradeDialogProvider>
        </Dialog>
    );
}

function EnableBillingUsageAddonDialogContent(props: EnableBillingUsageAddonDialogProps) {
    const { organization, meterName, onConfirm, site } = props;
    const interval = organization?.billing?.interval ?? api.BillingInterval.Monthly;
    const meterPrice = METER_PRICES[meterName];
    const closeDialog = useDialogClose();
    const canAdminBilling = organization.permissions.admin;
    const { onSubmit, trialStatus, requiredSiteType, upgrade } = useUpgradeAddon({
        organization,
        meterName,
        site,
        onSuccess: () => {
            onConfirm();
            closeDialog();
        },
    });
    const requiredSiteMonthlyPrice =
        site && organization.billing && requiredSiteType
            ? (getMonthlySitePrice({
                  siteType: requiredSiteType,
                  billing: organization.billing,
                  interval,
              }) ?? 0)
            : 0;

    const pricingParts = getBillingUsageAddonPricingParts({ meterPrice, interval });
    const totalMonthlyPrice = pricingParts.flatInitialMonthlyPrice + requiredSiteMonthlyPrice;
    const canStartTrial = trialStatus === api.BillingTrialStatus.None;
    const isFreeTrial = trialStatus === api.BillingTrialStatus.Active;

    return (
        <DialogBrandedLayout
            aside={{
                content: (
                    <div className="mt-auto">
                        <div>
                            <div className="flex flex-row items-center gap-2">
                                <PrettyDollarNumber
                                    value={totalMonthlyPrice}
                                    className="text-ui-heading-medium text-base"
                                />
                                <span className="text-ui-base text-secondary">/ month</span>
                            </div>
                        </div>
                        {requiredSiteType && requiredSiteMonthlyPrice > 0 ? (
                            <div className="text-ui-small text-muted">
                                {priceFormatter.format(requiredSiteMonthlyPrice)} for{' '}
                                {getSiteProductDefinition(requiredSiteType).title} +{' '}
                                {priceFormatter.format(pricingParts.flatInitialMonthlyPrice)} for
                                the add-on
                            </div>
                        ) : null}
                        <div className="text-ui-small text-secondary">
                            for the first {pricingParts.included.count}{' '}
                            {pricingParts.included.unitLabel}
                        </div>
                        <div className="text-ui-small text-secondary">
                            + <PrettyDollarNumber value={pricingParts.pricePerBatch} /> per extra{' '}
                            {pricingParts.additional.count
                                ? `${pricingParts.additional.count} `
                                : ''}
                            {pricingParts.additional.unitLabel}
                        </div>
                    </div>
                ),
            }}
        >
            <DialogHeader>
                <Heading>
                    Enable{' '}
                    {meterPrice.addon?.title ? `${meterPrice.addon?.title} add-on` : 'add-on'}
                </Heading>
            </DialogHeader>

            <DialogBody>
                <div className="flex flex-col gap-3">
                    {meterPrice.addon?.description ? (
                        <p className="text-ui-base text-secondary">
                            {meterPrice.addon.description}
                        </p>
                    ) : null}
                    {meterPrice.addon && meterPrice.addon.sellingPoints.length > 0 ? (
                        <ul className="flex flex-col gap-1">
                            {meterPrice.addon.sellingPoints.map((point) => (
                                <li key={point} className="flex flex-row items-start gap-2">
                                    <div className="flex h-4 w-4 items-center justify-center rounded-full bg-upgrade">
                                        <Icon.Check className="size-3 text-upgrade" />
                                    </div>
                                    <div className="flex-1 text-ui-base text-secondary">
                                        {point}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    ) : null}
                    {/* Only show this description if no upgrade is required, e.g. active subscriptions */}
                    {!upgrade ? (
                        <p className="text-ui-base text-secondary">
                            Enabling this add-on will add it to your billing subscription.
                        </p>
                    ) : null}
                    {canAdminBilling ? (
                        <>
                            {isFreeTrial ? (
                                <Alert
                                    kind="info"
                                    size="xsmall"
                                    details="Try this add-on for free during your trial. Once your trial ends, you can add it to your subscription."
                                />
                            ) : null}
                            {/* Active subscription, but site is not Ultimate */}
                            {requiredSiteType && !upgrade && !isFreeTrial ? (
                                <Alert
                                    kind="upgrade"
                                    size="xsmall"
                                    details={`This add-on is available on ${getSiteProductDefinition(requiredSiteType).title} sites only. Enabling it will automatically upgrade your site.`}
                                />
                            ) : null}
                            {/* No subscription and site is not Ultimate */}
                            {requiredSiteType && upgrade ? (
                                <Alert
                                    kind="upgrade"
                                    size="xsmall"
                                    details={`This add-on requires a subscription and the site must be on ${getSiteProductDefinition(requiredSiteType).title}. Enabling it will automatically upgrade your site.`}
                                />
                            ) : null}
                            {/* No subscription and either site is already Ultimate or we are not in a site's context */}
                            {!requiredSiteType && upgrade && !canStartTrial && !isFreeTrial ? (
                                <Alert
                                    kind="upgrade"
                                    size="xsmall"
                                    details={`You need a subscription before you can enable this add-on.`}
                                />
                            ) : null}
                        </>
                    ) : (
                        <Alert
                            kind="warning"
                            size="xsmall"
                            details="You don't have permission to enable add-ons. Contact an administrator to enable this add-on."
                        />
                    )}
                </div>
            </DialogBody>

            <DialogFooter>
                <DialogCloseButton />
                <Button
                    onPress={onSubmit}
                    kind="upgrade"
                    leadingIcon={Icon.Upgrade}
                    label={
                        canStartTrial && upgrade
                            ? 'Start trial'
                            : isFreeTrial
                              ? 'Try for free'
                              : upgrade || requiredSiteType
                                ? 'Upgrade and enable add-on'
                                : 'Enable add-on'
                    }
                    disabled={!canAdminBilling}
                />
            </DialogFooter>
        </DialogBrandedLayout>
    );
}
